<template>
  <v-card>
    <v-card-title class="primary">
      <span class="tertiary--text headline">{{ title }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh />
    </v-card-title>
    <v-card-text>
      <FormWrapper
        ref="formWrapper"
        formRef="scaleSheetForm"
        :buttonText="$t('saveProfile')"
        strongValidation
        :disabled="!enableSubmit"
        @submit="saveButtonClicked"
      >
        <v-container fluid grid-list-lg>
          <v-row dense>
            <v-col cols="12">
              <span class="mr-2 text-h6">
                {{ $t('profileName') }}
              </span>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="profile.name"
                :rules="[rules.required]"
                :label="$t('name')"
                outlined
                maxLength="64"
                counter="64"
                color="black"
              />
            </v-col>
            <v-col cols="12">
              <v-row dense align="center">
                <v-col cols="auto">
                  <span class="text-h6">
                    {{ $t('general') }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <Icon
                    icon="mdi-information-outline"
                    iconColor="info"
                    :tooltipText="$t('scaleSheetProfileGeneralTooltip')"
                    :small="false"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="3" lg="2">
              <v-autocomplete
                v-model="profile.scaleSheetImportFileType"
                :rules="[rules.requiredNum]"
                :items="fileTypes"
                :label="$t('fileType')"
                item-text="label"
                item-value="value"
                outlined
                color="black"
              >
                <template #selection="{ item }">
                  <span>
                    {{ $t(item.label) }}
                  </span>
                </template>
                <template #item="{ item }">
                  <span>
                    {{ $t(item.label) }}
                  </span>
                </template>
                <template #prepend-inner>
                  <Icon
                    icon="mdi-information-outline"
                    iconColor="info"
                    :tooltipText="getTooltipTextForFileType"
                    :small="false"
                  />
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" lg="2">
              <v-text-field
                v-model="profile.defaultInWeight"
                :rules="[rules.positiveIntegerBetweenOrBlank(1, constants.MAX_WEIGHT)]"
                :label="$t('defaultInWeight')"
                outlined
                maxLength="6"
                counter="6"
                color="black"
              />
            </v-col>
            <v-col cols="12" md="3" lg="2">
              <v-text-field
                v-model="profile.defaultOutWeight"
                :rules="[rules.positiveIntegerBetweenOrBlank(1, constants.MAX_WEIGHT)]"
                :label="$t('defaultOutWeight')"
                outlined
                maxLength="6"
                counter="6"
                color="black"
              />
            </v-col>
            <v-col cols="12" md="3" lg="2">
              <v-text-field
                v-model="profile.buttLogIndicator"
                :label="$t('buttLogIndicator')"
                outlined
                maxLength="32"
                counter="32"
                color="black"
              />
            </v-col>
            <v-col cols="12" md="3" lg="2">
              <v-text-field
                v-model="profile.multiplicationFactor"
                :rules="[rules.positiveIntegerBetweenOrBlank(constants.MULTIPLICATION_LOWER_BOUND, constants.MULTIPLICATION_UPPER_BOUND)]"
                :label="$t('multiplicationFactor')"
                outlined
                maxLength="3"
                counter="3"
                color="black"
              />
            </v-col>
            <v-col cols="12" md="3" lg="2">
              <v-autocomplete
                v-model="profile.defaultGradeId"
                :items="propGrades"
                :label="$t('defaultGrade')"
                item-text="name"
                item-value="gradeId"
                outlined
                color="black"
              />
            </v-col>
            <v-col cols="12" md="3" lg="2">
              <v-autocomplete
                v-model="profile.defaultSettingId"
                :items="propSettings"
                :label="$t('defaultSetting')"
                item-text="name"
                item-value="settingId"
                outlined
                color="black"
              />
            </v-col>
            <v-col cols="12" md="3" lg="2">
              <v-text-field
                v-model="profile.defaultTrailerIdentifier"
                :label="$t('defaultTrailerIdentifier')"
                outlined
                maxLength="10"
                counter="10"
                color="black"
              />
            </v-col>
            <v-col cols="12" v-if="profile.scaleSheetImportFileType === ScaleSheetImportFileType.Csv.value">
              <ScaleSheetImportCsvProfileForm
                :propProfile="profile.scaleSheetImportCsvProfile"
                @update="updateCsvProfile"
              />
            </v-col>
            <v-col cols="12" v-if="profile.scaleSheetImportFileType === ScaleSheetImportFileType.Delimited.value">
              <ScaleSheetImportDelimitedProfileForm
                :propProfile="profile.scaleSheetImportDelimitedProfile"
                @update="updateDelimitedProfile"
              />
            </v-col>
            <v-col cols="12" v-if="profile.scaleSheetImportFileType === ScaleSheetImportFileType.FixedLength.value">
              <ScaleSheetImportFixedLengthProfileForm
                :propProfile="profile.scaleSheetImportFixedLengthProfile"
                @update="updateFixedLengthProfile"
              />
            </v-col>
            <v-col cols="12" v-if="propProducts.length > 0">
              <v-row align="center">
                <v-col cols="auto">
                  <span class="mr-2 text-h6">
                    {{ $t('productProfiles') }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <Icon
                    icon="mdi-plus"
                    iconColor="primary"
                    :small="false"
                    :tooltipText="$t('addProductProfile')"
                    @icon-clicked="addProductProfile"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6" lg="3" v-for="(product, index) in profile.productProfiles" :key="index + ' product'">
              <v-card outlined rounded class="fill-height">
                <v-card-title>
                  <v-row align="center">
                    <v-col cols="auto">
                      <span class="subtitle-1">
                        {{ $t('productProfile') }} {{ index + 1 }}
                      </span>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-delete"
                        iconColor="error"
                        :small="false"
                        :tooltipText="$t('removeProductProfile')"
                        @icon-clicked="profile.productProfiles.splice(index, 1)"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="product.externalProductName"
                        :rules="[rules.required, rules.unique(profile.productProfiles.map(e => e.externalProductName), 'duplicateProductName')]"
                        :label="$t('productName')"
                        outlined
                        maxLength="32"
                        counter="32"
                        color="black"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="product.productId"
                        :items="propProducts"
                        :rules="[rules.required]"
                        item-text="name"
                        item-value="productId"
                        :label="$t('product')"
                        outlined
                        color="black"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" v-if="propSpecies.length > 0">
              <v-row align="center">
                <v-col cols="auto">
                  <span class="mr-2 text-h6">
                    {{ $t('speciesProfiles') }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <Icon
                    icon="mdi-plus"
                    iconColor="primary"
                    :small="false"
                    :tooltipText="$t('addSpeciesProfile')"
                    @icon-clicked="addSpeciesProfile"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6" lg="3" v-for="(species, index) in profile.speciesProfiles" :key="index + ' s'">
              <v-card outlined rounded class="fill-height">
                <v-card-title>
                  <v-row align="center">
                    <v-col cols="auto">
                      <span class="subtitle-1">
                        {{ $t('speciesProfile') }} {{ index + 1 }}
                      </span>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-delete"
                        iconColor="error"
                        :small="false"
                        :tooltipText="$t('removeSpeciesProfile')"
                        @icon-clicked="profile.speciesProfiles.splice(index, 1)"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="species.externalSpeciesCode"
                        :rules="[rules.required, rules.unique(profile.speciesProfiles.map(s => s.externalSpeciesCode), 'duplicateSpeciesCode')]"
                        :label="$t('speciesCode')"
                        outlined
                        maxLength="32"
                        counter="32"
                        color="black"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="species.speciesId"
                        :items="propSpecies"
                        :rules="[rules.required]"
                        item-text="commonName"
                        item-value="speciesId"
                        :label="$t('species')"
                        outlined
                        color="black"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" v-if="propGrades.length > 0">
              <v-row align="center">
                <v-col cols="auto">
                  <span class="mr-2 text-h6">
                    {{ $t('gradeProfiles') }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <Icon
                    icon="mdi-plus"
                    iconColor="primary"
                    :small="false"
                    :tooltipText="$t('addGradesProfile')"
                    @icon-clicked="addGradesProfile"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6" lg="3" v-for="(grade, index) in profile.gradeProfiles" :key="index + ' g'">
              <v-card outlined rounded class="fill-height">
                <v-card-title>
                  <v-row align="center">
                    <v-col cols="auto">
                      <span class="subtitle-1">
                        {{ $t('gradeProfile') }} {{ index + 1 }}
                      </span>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-delete"
                        iconColor="error"
                        :small="false"
                        :tooltipText="$t('removeGradeProfile')"
                        @icon-clicked="profile.gradeProfiles.splice(index, 1)"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="grade.externalGradeCode"
                        :rules="[rules.required, rules.unique(profile.gradeProfiles.map(g => g.externalGradeCode), 'duplicateGradeCode')]"
                        :label="$t('externalGrade')"
                        outlined
                        maxLength="32"
                        counter="32"
                        color="black"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="grade.gradeId"
                        :items="propGrades"
                        :rules="[rules.required]"
                        item-text="name"
                        item-value="gradeId"
                        :label="$t('grade')"
                        outlined
                        color="black"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" v-if="propEndUseSorts.length > 0">
              <v-row align="center">
                <v-col cols="auto">
                  <span class="mr-2 text-h6">
                    {{ $t('endUseSortProfiles') }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <Icon
                    icon="mdi-plus"
                    iconColor="primary"
                    :small="false"
                    :tooltipText="$t('addEndUseSortProfile')"
                    @icon-clicked="addEndUseSortProfile"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6" lg="3" v-for="(endUseSort, index) in profile.endUseSortProfiles" :key="index + ' eus'">
              <v-card outlined rounded class="fill-height">
                <v-card-title>
                  <v-row align="center">
                    <v-col cols="auto">
                      <span class="subtitle-1">
                        {{ $t('endUseSortProfile') }} {{ index + 1 }}
                      </span>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-delete"
                        iconColor="error"
                        :small="false"
                        :tooltipText="$t('removeEndUseSortProfile')"
                        @icon-clicked="profile.endUseSortProfiles.splice(index, 1)"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="endUseSort.externalEndUseSortCode"
                        :rules="[rules.required, rules.unique(profile.endUseSortProfiles.map(e => e.externalEndUseSortCode), 'duplicateEndUseSortCode')]"
                        :label="$t('externalEndUseSort')"
                        outlined
                        maxLength="32"
                        counter="32"
                        color="black"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="endUseSort.endUseSortId"
                        :items="propEndUseSorts"
                        :rules="[rules.required]"
                        item-text="name"
                        item-value="endUseSortId"
                        :label="$t('endUseSort')"
                        outlined
                        color="black"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" v-if="propAccounts.length > 0">
              <v-row align="center">
                <v-col cols="auto">
                  <v-row dense align="center">
                    <v-col cols="auto">
                      <span class="text-h6">
                        {{ $t('accountProfiles') }}
                      </span>
                    </v-col>
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-information-outline"
                        iconColor="info"
                        :tooltipText="$t('scaleSheetProfileAccountTooltip')"
                        :small="false"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="auto">
                  <Icon
                    icon="mdi-plus"
                    iconColor="primary"
                    :small="false"
                    :tooltipText="$t('addAccountProfile')"
                    @icon-clicked="addAccountProfile"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6" lg="3" v-for="(account, index) in profile.accountProfiles" :key="index + ' account'">
              <v-card outlined rounded class="fill-height">
                <v-card-title>
                  <v-row align="center">
                    <v-col cols="auto">
                      <span class="subtitle-1">
                        {{ $t('accountProfile') }} {{ index + 1 }}
                      </span>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-delete"
                        iconColor="error"
                        :small="false"
                        :tooltipText="$t('removeAccountProfile')"
                        @icon-clicked="profile.accountProfiles.splice(index, 1)"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="account.externalAccountName"
                        :rules="[rules.required, rules.unique(profile.accountProfiles.map(e => e.externalAccountName), 'duplicateAccountName')]"
                        :label="$t('accountName')"
                        outlined
                        maxLength="32"
                        counter="32"
                        color="black"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="account.accountId"
                        :items="propAccounts"
                        item-text="name"
                        :rules="[rules.required]"
                        item-value="accountId"
                        :label="$t('account')"
                        outlined
                        color="black"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" v-if="propSettings.length > 0">
              <v-row align="center">
                <v-col cols="auto">
                  <span class="mr-2 text-h6">
                    {{ $t('settingProfiles') }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <Icon
                    icon="mdi-plus"
                    iconColor="primary"
                    :small="false"
                    :tooltipText="$t('addSettingProfile')"
                    @icon-clicked="addSettingProfile"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6" lg="3" v-for="(setting, index) in profile.settingProfiles" :key="index + ' setting'">
              <v-card outlined rounded class="fill-height">
                <v-card-title>
                  <v-row align="center">
                    <v-col cols="auto">
                      <span class="subtitle-1">
                        {{ $t('settingProfile') }} {{ index + 1 }}
                      </span>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-delete"
                        iconColor="error"
                        :small="false"
                        :tooltipText="$t('removeSettingProfile')"
                        @icon-clicked="profile.settingProfiles.splice(index, 1)"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="setting.externalSettingName"
                        :rules="[rules.required, rules.unique(profile.settingProfiles.map(e => e.externalSettingName), 'duplicateSettingName')]"
                        :label="$t('setting')"
                        outlined
                        maxLength="32"
                        counter="32"
                        color="black"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="setting.settingId"
                        :items="propSettings"
                        :rules="[rules.required]"
                        item-text="name"
                        item-value="settingId"
                        :label="$t('setting')"
                        outlined
                        color="black"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" v-if="propTracts.length > 0">
              <v-row align="center">
                <v-col cols="auto">
                  <span class="mr-2 text-h6">
                    {{ $t('tractProfiles') }}
                  </span>
                </v-col>
                <v-col cols="auto">
                  <Icon
                    icon="mdi-plus"
                    iconColor="primary"
                    :small="false"
                    :tooltipText="$t('addTractProfile')"
                    @icon-clicked="addTractProfile"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6" lg="3" v-for="(tract, index) in profile.tractProfiles" :key="index + ' tract'">
              <v-card outlined rounded class="fill-height">
                <v-card-title>
                  <v-row align="center">
                    <v-col cols="auto">
                      <span class="subtitle-1">
                        {{ $t('tractProfile') }} {{ index + 1 }}
                      </span>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <Icon
                        icon="mdi-delete"
                        iconColor="error"
                        :small="false"
                        :tooltipText="$t('removeTractProfile')"
                        @icon-clicked="profile.tractProfiles.splice(index, 1)"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="tract.externalTractName"
                        :rules="[rules.required, rules.unique(profile.tractProfiles.map(e => e.externalTractName), 'duplicateTractName')]"
                        :label="$t('tractName')"
                        outlined
                        maxLength="32"
                        counter="32"
                        color="black"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="tract.tractId"
                        :items="propTracts"
                        :rules="[rules.required]"
                        item-text="name"
                        item-value="tractId"
                        :label="$t('tract')"
                        outlined
                        color="black"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import fieldRules from '@/utils/rules.js'
import { isEqual } from 'lodash'
import { ScaleSheetImportFileType } from '@/utils/Enumerations'
export default {
  name: 'ScaleSheetForm',

  props: {
    propProfile: {
      type: Object,
      default: undefined
    },
    propSpecies: {
      type: Array,
      default: () => []
    },
    propGrades: {
      type: Array,
      default: () => []
    },
    propEndUseSorts: {
      type: Array,
      default: () => []
    },
    propAccounts: {
      type: Array,
      default: () => []
    },
    propSettings: {
      type: Array,
      default: () => []
    },
    propTracts: {
      type: Array,
      default: () => []
    },
    propProducts: {
      type: Array,
      default: () => []
    }
  },

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ScaleSheetImportCsvProfileForm: () => import('@/components/settings-components/imports/scale-sheet-import-components/ScaleSheetImportCsvProfileForm.vue'),
    ScaleSheetImportFixedLengthProfileForm: () => import('@/components/settings-components/imports/scale-sheet-import-components/ScaleSheetImportFixedLengthProfileForm.vue'),
    ScaleSheetImportDelimitedProfileForm: () => import('@/components/settings-components/imports/scale-sheet-import-components/ScaleSheetImportDelimitedProfileForm.vue')
  },

  data: () => ({
    rules: fieldRules.rules,
    profile: {
      name: '',
      scaleSheetImportProfileId: 0,
      scaleSheetImportFileType: '',
      scaleSheetImportCsvProfile: {
        ticketNumber: '',
        setting: null,
        tract: null,
        destination: null,
        contractAccount: null,
        product: null,
        weighedOutAt: null,
        inWeight: '',
        outWeight: '',
        netWeight: '',
        segmentOneGrade: null,
        segmentOneEndUseSort: null,
        segmentOneDiameter: '',
        segmentOneLength: '',
        segmentOneGrossVolume: null,
        segmentOneNetVolume: '',
        segmentOneDefectVolume: null,
        segmentTwoGrade: null,
        segmentTwoEndUseSort: null,
        segmentTwoDiameter: null,
        segmentTwoLength: null,
        segmentTwoGrossVolume: null,
        segmentTwoNetVolume: null,
        segmentTwoDefectVolume: null,
        segmentThreeDiameter: null,
        segmentThreeLength: null,
        segmentThreeGrossVolume: null,
        segmentThreeNetVolume: null,
        species: '',
        buttLog: '',
        trailerIdentifier: null
      },
      scaleSheetImportDelimitedProfile: {
        delimiter: '',
        headerPrefix: '',
        virtualPrefix: null,
        settingIndex: null,
        tractIndex: null,
        destinationIndex: null,
        contractAccountIndex: null,
        productIndex: null,
        weighedOutAtIndex: null,
        inWeightIndex: null,
        outWeightIndex: null,
        netWeightIndex: null,
        logPrefix: null,
        ticketNumberIndex: 0,
        speciesIndex: 0,
        segmentOneGradeIndex: null,
        segmentOneEndUseSortIndex: null,
        segmentOneLengthIndex: 0,
        segmentOneDiameterIndex: 0,
        segmentOneGrossVolumeIndex: null,
        segmentOneNetVolumeIndex: 0,
        segmentOneDefectVolumeIndex: null,
        segmentTwoGradeIndex: null,
        segmentTwoEndUseSortIndex: null,
        segmentTwoLengthIndex: null,
        segmentTwoDiameterIndex: null,
        segmentTwoGrossVolumeIndex: null,
        segmentTwoNetVolumeIndex: null,
        segmentTwoDefectVolumeIndex: null,
        segmentThreeLengthIndex: null,
        segmentThreeDiameterIndex: null,
        segmentThreeGrossVolumeIndex: null,
        segmentThreeNetVolumeIndex: null,
        buttLogIndex: 0,
        trailerIdentifierIndex: null
      },
      scaleSheetImportFixedLengthProfile: {
        skipTop: 0,
        skipBottom: 0,
        skipBottomExpanded: null,
        readUntil: '',
        ticketNumberLine: 0,
        ticketNumberStart: 0,
        ticketNumberLength: 0,
        settingLine: null,
        settingStart: null,
        settingLength: null,
        tractLine: null,
        tractStart: null,
        tractLength: null,
        destinationLine: null,
        destinationStart: null,
        destinationLength: null,
        contractAccountLine: null,
        contractAccountStart: null,
        contractAccountLength: null,
        productLine: null,
        productStart: null,
        productLength: null,
        weighedOutAtLine: null,
        weighedOutAtStart: null,
        weighedOutAtLength: null,
        inWeightLine: null,
        inWeightStart: null,
        inWeightLength: null,
        outWeightLine: null,
        outWeightStart: null,
        outWeightLength: null,
        netWeightLine: 0,
        netWeightStart: 0,
        netWeightLength: 0,
        segmetOneGradeStart: null,
        segmentOneGradeLength: null,
        segmentOneEndUseSortStart: null,
        segmentOneEndUseSortLength: null,
        segmentOneDiameterStart: 0,
        segmentOneDiameterLength: 0,
        segmentOneLengthStart: 0,
        segmentOneLengthLength: 0,
        segmentOneGrossVolumeStart: null,
        segmentOneGrossVolumeLength: null,
        segmentOneNetVolumeStart: 0,
        segmentOneNetVolumeLength: 0,
        segmentOneDefectVolumeStart: null,
        segmentOneDefectVolumeLength: null,
        segmentTwoGradeStart: null,
        segmentTwoGradeLength: null,
        segmentTwoEndUseSortStart: null,
        segmentTwoEndUseSortLength: null,
        segmentTwoDiameterStart: null,
        segmentTwoDiameterLength: null,
        segmentTwoLengthStart: null,
        segmentTwoLengthLength: null,
        segmentTwoGrossVolumeStart: null,
        segmentTwoGrossVolumeLength: null,
        segmentTwoNetVolumeStart: null,
        segmentTwoNetVolumeLength: null,
        segmentTwoDefectVolumeStart: null,
        segmentTwoDefectVolumeLength: null,
        segmentThreeDiameterStart: null,
        segmentThreeDiameterLength: null,
        segmentThreeLengthStart: null,
        segmentThreeLengthLength: null,
        segmentThreeGrossVolumeStart: null,
        segmentThreeGrossVolumeLength: null,
        segmentThreeNetVolumeStart: null,
        segmentThreeNetVolumeLength: null,
        speciesStart: 0,
        speciesLength: 0,
        buttLogStart: 0,
        buttLogLength: 0,
        trailerIdentifierLine: null,
        trailerIdentifierStart: null,
        trailerIdentifierLength: null
      },
      defaultInWeight: null,
      defaultOutWeight: null,
      buttLogIndicator: null,
      multiplicationFactor: null,
      defaultGradeId: null,
      defaultGrade: null,
      defaultSettingId: null,
      defaultSetting: null,
      defaultTrailerIdentifier: null,
      speciesProfiles: [],
      gradeProfiles: [],
      endUseSortProfiles: [],
      accountProfiles: [],
      settingProfiles: [],
      tractProfiles: [],
      productProfiles: []
    },
    staleProfile: undefined,
    ScaleSheetImportFileType
  }),

  created () {
    if (this.propProfile) {
      this.profile = JSON.parse(JSON.stringify(this.propProfile))
      if (!this.profile.scaleSheetImportFixedLengthProfile) {
        this.profile.scaleSheetImportFixedLengthProfile = {
          skipTop: 0,
          skipBottom: 0,
          skipBottomExpanded: null,
          readUntil: '',
          ticketNumberLine: 0,
          ticketNumberStart: 0,
          ticketNumberLength: 0,
          settingLine: null,
          settingStart: null,
          settingLength: null,
          tractLine: null,
          tractStart: null,
          tractLength: null,
          destinationLine: null,
          destinationStart: null,
          destinationLength: null,
          contractAccountLine: null,
          contractAccountStart: null,
          contractAccountLength: null,
          productLine: null,
          productStart: null,
          productLength: null,
          weighedOutAtLine: null,
          weighedOutAtStart: null,
          weighedOutAtLength: null,
          inWeightLine: null,
          inWeightStart: null,
          inWeightLength: null,
          outWeightLine: null,
          outWeightStart: null,
          outWeightLength: null,
          netWeightLine: 0,
          netWeightStart: 0,
          netWeightLength: 0,
          logNumberStart: 0,
          logNumberLength: 0,
          segmentOneGradeStart: null,
          segmentOneGradeLength: null,
          segmentOneEndUseSortStart: null,
          segmentOneEndUseSortLength: null,
          segmentOneDiameterStart: 0,
          segmentOneDiameterLength: 0,
          segmentOneLengthStart: 0,
          segmentOneLengthLength: 0,
          segmentOneGrossVolumeStart: null,
          segmentOneGrossVolumeLength: null,
          segmentOneNetVolumeStart: 0,
          segmentOneNetVolumeLength: 0,
          segmentOneDefectVolumeStart: null,
          segmentOneDefectVolumeLength: null,
          segmentTwoGradeStart: null,
          segmentTwoGradeLength: null,
          segmentTwoEndUseSortStart: null,
          segmentTwoEndUseSortLength: null,
          segmentTwoDiameterStart: null,
          segmentTwoDiameterLength: null,
          segmentTwoLengthStart: null,
          segmentTwoLengthLength: null,
          segmentTwoGrossVolumeStart: null,
          segmentTwoGrossVolumeLength: null,
          segmentTwoNetVolumeStart: null,
          segmentTwoNetVolumeLength: null,
          segmentThreeDiameterStart: null,
          segmentThreeDiameterLength: null,
          segmentThreeLengthStart: null,
          segmentThreeLengthLength: null,
          segmentThreeGrossVolumeStart: null,
          segmentThreeGrossVolumeLength: null,
          segmentThreeNetVolumeStart: null,
          segmentThreeNetVolumeLength: null,
          speciesStart: 0,
          speciesLength: 0,
          gradeStart: 0,
          gradeLength: 0,
          endUseSortStart: 0,
          endUseSortLength: 0,
          buttLogStart: 0,
          buttLogLength: 0,
          trailerIdentifierLine: null,
          trailerIdentifierStart: null,
          trailerIdentifierLength: null
        }
      }
      if (!this.profile.scaleSheetImportCsvProfile) {
        this.profile.scaleSheetImportCsvProfile = {
          ticketNumber: '',
          setting: null,
          tract: null,
          destination: null,
          contractAccount: null,
          product: null,
          weighedOutAt: null,
          inWeight: null,
          outWeight: null,
          netWeight: null,
          logNumber: '',
          segmentOneGrade: null,
          segmentOneEndUseSort: null,
          segmentOneDiameter: '',
          segmentOneLength: '',
          segmentOneGrossVolume: null,
          segmentOneNetVolume: '',
          segmentOneDefectVolume: null,
          segmentTwoGrade: null,
          segmentTwoEndUseSort: null,
          segmentTwoDiameter: null,
          segmentTwoLength: null,
          segmentTwoGrossVolume: null,
          segmentTwoNetVolume: null,
          segmentTwoDefectVolume: null,
          segmentThreeDiameter: null,
          segmentThreeLength: null,
          segmentThreeGrossVolume: null,
          segmentThreeNetVolume: null,
          species: '',
          buttLog: '',
          trailerIdentifier: null
        }
      }
      if (!this.profile.scaleSheetImportDelimitedProfile) {
        this.profile.scaleSheetImportDelimitedProfile = {
          delimiter: '',
          headerPrefix: '',
          virtualPrefix: null,
          settingIndex: null,
          tractIndex: null,
          destinationIndex: null,
          contractAccountIndex: null,
          productIndex: null,
          weighedOutAtIndex: null,
          inWeightIndex: null,
          outWeightIndex: null,
          netWeightIndex: null,
          logPrefix: null,
          ticketNumberIndex: 0,
          speciesIndex: 0,
          segmentOneGradeIndex: null,
          segmentOneEndUseSortIndex: null,
          segmentOneLengthIndex: 0,
          segmentOneDiameterIndex: 0,
          segmentOneGrossVolumeIndex: null,
          segmentOneNetVolumeIndex: 0,
          segmentOneDefectVolumeIndex: null,
          segmentTwoGradeIndex: null,
          segmentTwoEndUseSortIndex: null,
          segmentTwoLengthIndex: null,
          segmentTwoDiameterIndex: null,
          segmentTwoGrossVolumeIndex: null,
          segmentTwoNetVolumeIndex: null,
          segmentTwoDefectVolumeIndex: null,
          segmentThreeLengthIndex: null,
          segmentThreeDiameterIndex: null,
          segmentThreeGrossVolumeIndex: null,
          segmentThreeNetVolumeIndex: null,
          buttLogIndex: 0,
          trailerIdentifierIndex: null
        }
      }
      this.staleProfile = JSON.parse(JSON.stringify(this.profile))
    }
  },

  computed: {
    constants () {
      return {
        MAX_LINE_LENGTH: 1000,
        MAX_WEIGHT: 999999,
        MULTIPLICATION_LOWER_BOUND: 2,
        MULTIPLICATION_UPPER_BOUND: 100
      }
    },
    enableSubmit () {
      const formChanged = !isEqual(this.profile, this.staleProfile)
      return formChanged && this.profile.speciesProfiles.length > 0
    },
    title () {
      return this.profile.scaleSheetImportProfileId ? this.$t('editing', { name: this.propProfile.name }) : this.$t('createScaleSheetProfile')
    },
    fileTypes () {
      return ScaleSheetImportFileType.enums
    },
    getTooltipTextForFileType () {
      switch (this.profile.scaleSheetImportFileType) {
        case ScaleSheetImportFileType.Csv.value:
          return this.$t('scaleSheetImportCsvTooltipText')
        case ScaleSheetImportFileType.Delimited.value:
          return this.$t('scaleSheetImportDelimitedTooltipText')
        case ScaleSheetImportFileType.FixedLength.value:
          return this.$t('scaleSheetImportFixedLengthTooltipText')
        default:
          return this.$t('selectFileTypeTooltipText')
      }
    }
  },

  watch: {
    'profile.productProfiles': {
      deep: true,
      handler () { this.revalidateForm() }
    },
    'profile.speciesProfiles': {
      deep: true,
      handler () { this.revalidateForm() }
    },
    'profile.gradeProfiles': {
      deep: true,
      handler () { this.revalidateForm() }
    },
    'profile.endUseSortProfiles': {
      deep: true,
      handler () { this.revalidateForm() }
    },
    'profile.accountProfiles': {
      deep: true,
      handler () { this.revalidateForm() }
    },
    'profile.settingProfiles': {
      deep: true,
      handler () { this.revalidateForm() }
    },
    'profile.tractProfiles': {
      deep: true,
      handler () { this.revalidateForm() }
    }
  },

  methods: {
    saveButtonClicked () {
      this.$emit('save', this.sanitizeProfileData(this.profile))
    },
    sanitizeProfileData (profile) {
      const sanitizedProfile = { ...profile }
      if (sanitizedProfile.buttLogIndicator === '') {
        sanitizedProfile.buttLogIndicator = null
      }
      if (sanitizedProfile.defaultTrailerIdentifier === '') {
        sanitizedProfile.defaultTrailerIdentifier = null
      }
      if (sanitizedProfile.scaleSheetImportFileType === ScaleSheetImportFileType.Csv.value) {
        sanitizedProfile.scaleSheetImportFixedLengthProfile = null
        sanitizedProfile.scaleSheetImportDelimitedProfile = null
        if (!this.hasTwoSegments(sanitizedProfile)) {
          sanitizedProfile.scaleSheetImportCsvProfile = {
            ...sanitizedProfile.scaleSheetImportCsvProfile,
            segmentTwoDiameter: null,
            segmentTwoLength: null,
            segmentTwoGrossVolume: null,
            segmentTwoNetVolume: null,
            segmentTwoDefectVolume: null,
            segmentTwoGrade: null,
            segmentTwoEndUseSort: null
          }
        }
        if (!this.hasThreeSegments(sanitizedProfile)) {
          sanitizedProfile.scaleSheetImportCsvProfile = {
            ...sanitizedProfile.scaleSheetImportCsvProfile,
            segmentThreeDiameter: null,
            segmentThreeLength: null,
            segmentThreeGrossVolume: null,
            segmentThreeNetVolume: null
          }
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.inWeight === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.inWeight = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.outWeight === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.outWeight = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.tract === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.tract = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.setting === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.setting = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.destination === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.destination = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.contractAccount === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.contractAccount = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.trailerIdentifier === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.trailerIdentifier = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentOneGrossVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentOneGrossVolume = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentOneNetVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentOneNetVolume = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentOneDefectVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentOneDefectVolume = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoGrossVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoGrossVolume = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoNetVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoNetVolume = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoDefectVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoDefectVolume = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentOneGrade === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentOneGrade = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentOneEndUseSort === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentOneEndUseSort = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoGrade === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoGrade = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoEndUseSort === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentTwoEndUseSort = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.weighedOutAt === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.weighedOutAt = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.product === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.product = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentThreeGrossVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentThreeGrossVolume = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentThreeNetVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentThreeNetVolume = null
        }
        if (sanitizedProfile.scaleSheetImportCsvProfile.segmentThreeDefectVolume === '') {
          sanitizedProfile.scaleSheetImportCsvProfile.segmentThreeDefectVolume = null
        }
      } else if (sanitizedProfile.scaleSheetImportFileType === ScaleSheetImportFileType.FixedLength.value) {
        sanitizedProfile.scaleSheetImportCsvProfile = null
        sanitizedProfile.scaleSheetImportDelimitedProfile = null
        if (!this.hasTwoSegments(sanitizedProfile)) {
          sanitizedProfile.scaleSheetImportFixedLengthProfile = {
            ...sanitizedProfile.scaleSheetImportFixedLengthProfile,
            segmentTwoDiameterStart: null,
            segmentTwoDiameterLength: null,
            segmentTwoLengthStart: null,
            segmentTwoLengthLength: null,
            segmentTwoGrossVolumeStart: null,
            segmentTwoGrossVolumeLength: null,
            segmentTwoNetVolumeStart: null,
            segmentTwoNetVolumeLength: null,
            segmentTwoDefectVolumeStart: null,
            segmentTwoDefectVolumeLength: null,
            segmentTwoGradeStart: null,
            segmentTwoGradeLength: null
          }
        }
        if (!this.hasThreeSegments(sanitizedProfile)) {
          sanitizedProfile.scaleSheetImportFixedLengthProfile = {
            ...sanitizedProfile.scaleSheetImportFixedLengthProfile,
            segmentThreeDiameterStart: null,
            segmentThreeDiameterLength: null,
            segmentThreeLengthStart: null,
            segmentThreeLengthLength: null,
            segmentThreeGrossVolumeStart: null,
            segmentThreeGrossVolumeLength: null,
            segmentThreeNetVolumeStart: null,
            segmentThreeNetVolumeLength: null
          }
        }
      } else {
        sanitizedProfile.scaleSheetImportFixedLengthProfile = null
        sanitizedProfile.scaleSheetImportCsvProfile = null
        if (sanitizedProfile.scaleSheetImportDelimitedProfile.virtualPrefix) {
          sanitizedProfile.scaleSheetImportDelimitedProfile = {
            ...sanitizedProfile.scaleSheetImportDelimitedProfile,
            headerPrefix: null,
            logPrefix: null
          }
        } else {
          sanitizedProfile.scaleSheetImportDelimitedProfile = {
            ...sanitizedProfile.scaleSheetImportDelimitedProfile,
            virtualPrefix: null
          }
        }
        if (!this.hasTwoSegments(sanitizedProfile)) {
          sanitizedProfile.scaleSheetImportDelimitedProfile = {
            ...sanitizedProfile.scaleSheetImportDelimitedProfile,
            segmentTwoLengthIndex: null,
            segmentTwoDiameterIndex: null,
            segmentTwoGrossVolumeIndex: null,
            segmentTwoNetVolumeIndex: null,
            segmentTwoDefectVolumeIndex: null,
            segmentTwoGradeIndex: null,
            segmentTwoEndUseSortIndex: null
          }
        }
        if (!this.hasThreeSegments(sanitizedProfile)) {
          sanitizedProfile.scaleSheetImportDelimitedProfile = {
            ...sanitizedProfile.scaleSheetImportDelimitedProfile,
            segmentThreeLengthIndex: null,
            segmentThreeDiameterIndex: null,
            segmentThreeGrossVolumeIndex: null,
            segmentThreeNetVolumeIndex: null
          }
        }
      }
      return sanitizedProfile
    },
    addSpeciesProfile () {
      this.profile.speciesProfiles.push({
        externalSpeciesCode: '',
        speciesId: undefined,
        scaleSheetImportProfileId: this.profile?.scaleSheetImportProfileId
      })
    },
    addGradesProfile () {
      this.profile.gradeProfiles.push({
        externalGradeCode: '',
        gradeId: undefined,
        scaleSheetImportProfileId: this.profile?.scaleSheetImportProfileId
      })
    },
    addEndUseSortProfile () {
      this.profile.endUseSortProfiles.push({
        externalEndUseSortCode: '',
        endUseSortId: undefined,
        scaleSheetImportProfileId: this.profile?.scaleSheetImportProfileId
      })
    },
    addAccountProfile () {
      this.profile.accountProfiles.push({
        externalAccountName: '',
        accountId: undefined,
        scaleSheetImportProfileId: this.profile?.scaleSheetImportProfileId
      })
    },
    addSettingProfile () {
      this.profile.settingProfiles.push({
        externalSettingName: '',
        settingId: undefined,
        scaleSheetImportProfileId: this.profile?.scaleSheetImportProfileId
      })
    },
    addTractProfile () {
      this.profile.tractProfiles.push({
        externalTractName: '',
        tractId: undefined,
        scaleSheetImportProfileId: this.profile?.scaleSheetImportProfileId
      })
    },
    addProductProfile () {
      this.profile.productProfiles.push({
        externalProductCode: '',
        productId: undefined,
        scaleSheetImportProfileId: this.profile?.scaleSheetImportProfileId
      })
    },
    updateCsvProfile (updatedProfile) {
      this.profile.scaleSheetImportCsvProfile = updatedProfile
    },
    updateFixedLengthProfile (updatedProfile) {
      this.profile.scaleSheetImportFixedLengthProfile = updatedProfile
    },
    updateDelimitedProfile (updatedProfile) {
      this.profile.scaleSheetImportDelimitedProfile = updatedProfile
    },
    hasTwoSegments (profile) {
      if (profile.scaleSheetImportFileType === ScaleSheetImportFileType.Csv.value) {
        return !!(!this.isNullOrEmpty(profile.scaleSheetImportCsvProfile.segmentTwoDiameter) &&
                !this.isNullOrEmpty(profile.scaleSheetImportCsvProfile.segmentTwoLength) &&
                (!this.isNullOrEmpty(profile.scaleSheetImportCsvProfile.segmentTwoNetVolume) ||
                !this.isNullOrEmpty(profile.scaleSheetImportCsvProfile.segmentTwoGrossVolume)))
      } else if (profile.scaleSheetImportFileType === ScaleSheetImportFileType.FixedLength.value) {
        return !!(!this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentTwoDiameterStart) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentTwoDiameterLength) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentTwoLengthStart) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentTwoLengthLength) &&
                ((!this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentTwoGrossVolumeStart) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentTwoGrossVolumeLength)) ||
                (!this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentTwoNetVolumeStart) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentTwoNetVolumeLength))))
      } else if (profile.scaleSheetImportFileType === ScaleSheetImportFileType.Delimited.value) {
        return !!(!this.isNullOrEmpty(profile.scaleSheetImportDelimitedProfile.segmentTwoDiameterIndex) &&
                !this.isNullOrEmpty(profile.scaleSheetImportDelimitedProfile.segmentTwoLengthIndex) &&
                (!this.isNullOrEmpty(profile.scaleSheetImportDelimitedProfile.segmentTwoNetVolumeIndex) ||
                !this.isNullOrEmpty(profile.scaleSheetImportDelimitedProfile.segmentTwoGrossVolumeIndex)))
      }
    },
    hasThreeSegments (profile) {
      if (profile.scaleSheetImportFileType === ScaleSheetImportFileType.Csv.value) {
        return !!(!this.isNullOrEmpty(profile.scaleSheetImportCsvProfile.segmentThreeDiameter) &&
                !this.isNullOrEmpty(profile.scaleSheetImportCsvProfile.segmentThreeLength) &&
                (!this.isNullOrEmpty(profile.scaleSheetImportCsvProfile.segmentThreeGrossVolume) ||
                !this.isNullOrEmpty(profile.scaleSheetImportCsvProfile.segmentThreeNetVolume)))
      } else if (profile.scaleSheetImportFileType === ScaleSheetImportFileType.FixedLength.value) {
        return !!(!this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentThreeDiameterStart) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentThreeDiameterLength) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentThreeLengthStart) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentThreeLengthLength) &&
                ((!this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentThreeGrossVolumeStart) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentThreeGrossVolumeLength)) ||
                (!this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentThreeNetVolumeStart) &&
                !this.isNullOrEmpty(profile.scaleSheetImportFixedLengthProfile.segmentThreeNetVolumeLength))))
      } else if (profile.scaleSheetImportFileType === ScaleSheetImportFileType.Delimited.value) {
        return !!(!this.isNullOrEmpty(profile.scaleSheetImportDelimitedProfile.segmentThreeDiameterIndex) &&
                !this.isNullOrEmpty(profile.scaleSheetImportDelimitedProfile.segmentThreeLengthIndex) &&
                (!this.isNullOrEmpty(profile.scaleSheetImportDelimitedProfile.segmentThreeGrossVolumeIndex) ||
                !this.isNullOrEmpty(profile.scaleSheetImportDelimitedProfile.segmentThreeNetVolumeIndex)))
      }
    },
    revalidateForm () {
      this.$nextTick(() => {
        const form = this.$refs.formWrapper
        if (form) {
          form.validate()
        }
      })
    },
    isNullOrEmpty: value => value === null || value === undefined || value === ''
  }
}
</script>
